import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button, SearchSelect } from '../shared';
import DatePicker from '../client/DatePicker';
import { useAdminAnnouncements } from '../../hooks/useAdminAnnouncements';
import { useToastContext } from '../../contexts/ToastContext';

const AdminAnnouncementModal = ({ isOpen, onClose, announcement = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    announcement_type: 'general',
    target_audience: 'all',
    is_active: true,
    start_date: '',
    end_date: ''
  });
  const [errors, setErrors] = useState({});
  const { createAnnouncement, updateAnnouncement, isCreating, isUpdating } = useAdminAnnouncements();
  const toast = useToastContext();

  const announcementTypes = [
    { value: 'general', label: 'General' },
    { value: 'update', label: 'Update' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'promotion', label: 'Promotion' },
    { value: 'important', label: 'Important' }
  ];

  const targetAudiences = [
    { value: 'all', label: 'All Users' },
    { value: 'clients', label: 'Clients' },
    { value: 'vendors', label: 'Vendors' },
    { value: 'admins', label: 'Admins' }
  ];

  useEffect(() => {
    if (isOpen) {
      if (announcement) {
        setFormData({
          title: announcement.title || '',
          content: announcement.content || '',
          announcement_type: announcement.announcement_type || 'general',
          target_audience: announcement.target_audience || 'all',
          is_active: announcement.is_active ?? true,
          start_date: announcement.start_date || '',
          end_date: announcement.end_date || ''
        });
      } else {
        setFormData({
          title: '',
          content: '',
          announcement_type: 'general',
          target_audience: 'all',
          is_active: true,
          start_date: '',
          end_date: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, announcement]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (formData.end_date && formData.start_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      newErrors.end_date = 'End date must be after start date';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    try {
      const data = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      if (announcement) {
        await updateAnnouncement({ id: announcement.id, data });
        toast.success(null, 'Announcement updated successfully');
      } else {
        await createAnnouncement(data);
        toast.success(null, 'Announcement created successfully');
      }
      
      onClose();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to save announcement';
      toast.error(null, errorMessage);
      
      // Set field errors if provided
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-surface rounded-lg shadow-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-surface border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-gray-900">
              {announcement ? 'Edit Announcement' : 'Create Announcement'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${ errors.title ? 'border-red-500' : 'border-gray-300' }`}
                placeholder="Enter announcement title"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${ errors.content ? 'border-red-500' : 'border-gray-300' }`}
                placeholder="Enter announcement content"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content}</p>
              )}
            </div>

            {/* Type and Target Audience */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SearchSelect
                  label="Announcement Type"
                  options={announcementTypes}
                  value={formData.announcement_type}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, announcement_type: value }));
                    if (errors.announcement_type) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.announcement_type;
                        return newErrors;
                      });
                    }
                  }}
                  searchable={false}
                  placeholder="Select announcement type"
                />
              </div>

              <div>
                <SearchSelect
                  label="Target Audience"
                  options={targetAudiences}
                  value={formData.target_audience}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, target_audience: value }));
                    if (errors.target_audience) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.target_audience;
                        return newErrors;
                      });
                    }
                  }}
                  searchable={false}
                  placeholder="Select target audience"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date (Optional)
                </label>
                <DatePicker
                  value={formData.start_date}
                  onChange={(value) => handleDateChange('start_date', value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date (Optional)
                </label>
                <DatePicker
                  value={formData.end_date}
                  onChange={(value) => handleDateChange('end_date', value)}
                />
                {errors.end_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
                )}
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
              <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                Active (announcement will be visible to users)
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isCreating || isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isCreating || isUpdating}
                disabled={isCreating || isUpdating}
              >
                {announcement ? 'Update Announcement' : 'Create Announcement'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncementModal;

